import { FC, useEffect, useMemo, useState } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EuroIcon from "@mui/icons-material/Euro";
import CheckIcon from "@mui/icons-material/Check";
import { Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import UndoIcon from "@mui/icons-material/Undo";
import RestrictedPage from "@/consumer/restrictedPage";
import styles from "@/components/trackedService/trackedService.module.css";
import { getTranslatedStatusOptions } from "@/helper/statusOptions";
import { getTrackedServiceColumns } from "@/components/trackedService/trackedServiceColumns";
import {
  getKaerEmployees,
  getMainCategories,
  getTrackedServiceTemplate,
  getTrackedServiceType,
} from "@/components/trackedService/trackedServiceFilterOptions";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import DateRangeFilterModal from "@/components/dateTime/dateRangeFilterModal";
import DateRangeFilterChip from "@/components/dateTime/dateRangeFilterChip";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import {
  clearAdditionalFilteringParameters,
  setAdditionalFilteringParameters,
} from "@/components/state/tableSearch/tableConfigSlice";
import { trackedServiceEntityAdapter } from "@/components/trackedService/trackedServiceEntityAdapter";

const TrackedServiceMainTable: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("trackedservice_recorded"));
  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const [mainCategoryOptionsForFiltering, setMaincategoryOptionsForFiltering] =
    useState<any[]>([]);
  const [trackedServiceTypeForFiltering, setTrackedServiceTypeForFiltering] =
    useState<any[]>([]);
  const [
    trackedServiceTemplateForFiltering,
    setTrackedServiceTemplateForFiltering,
  ] = useState<any[]>([]);

  const [kaerEmployeeForFiltering, setKaerEmployeeForFiltering] = useState<
    any[]
  >([]);
  const { handleError, errorModal } = useErrorModal();
  const [totalPriceInCents, setTotalPriceInCents] = useState<number | null>(
    null
  );
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);

  const globalAdditionalFilterParameters: any = useSelector(
    (state: RootState | any) => state.tableConfig?.["trackedService"]
  );

  const [filterItemForApiCall, setFilterItemForApiCall] = useState(() =>
    ({
      filter: globalAdditionalFilterParameters?.filter ?? "executed_on",
      includeTestCompanies:
        globalAdditionalFilterParameters?.includeTestCompanies ?? false,
      dateRange:
        globalAdditionalFilterParameters?.dateRange?.map((date: any) =>
          date ? dayjs(date).toDate() : null
        ) ?? [],
    })
  );

  const { showConfirmationModalAsync } = useConfirmationModal();
  const statusFilterOptions = getTranslatedStatusOptions("appointment");

  const yesNoFilterOptions = [
    { value: "true", label: t("yes") },
    { value: "false", label: t("no") },
  ];

  const dispatch = useDispatch();

  const additionalUrlParameters = useMemo(
    () => generateUrlParams(filterItemForApiCall),
    [filterItemForApiCall]
  );

  function generateUrlParams(filterItem: any): Record<string, string> {
    const params: Record<string, string> = {};

    if (filterItem.filter !== "" && filterItem.dateRange?.length > 0) {
      const [start, end] = filterItem.dateRange;
      const key = filterItem.filter;

      if (start) {
        params[`${key}__gt`] = dayjs(start).startOf("day").toISOString();
      }
      if (end) {
        params[`${key}__lt`] = dayjs(end).endOf("day").toISOString();
      }
    }
    if (typeof filterItem.includeTestCompanies === "boolean") {
      params["include_test_companies"] = filterItem.includeTestCompanies;
    }

    return params;
  }
  useEffect(() => {
    const hasDateFilter =
      filterItemForApiCall.filter !== "" &&
      filterItemForApiCall.dateRange?.some(Boolean);
    const activeFilters: any = {};

    dispatch(clearAdditionalFilteringParameters({ entity: "trackedService" }));

    if (hasDateFilter) {
      const [startDateRaw, endDateRaw] = filterItemForApiCall.dateRange || [];
      activeFilters.filter = filterItemForApiCall.filter;
      activeFilters.dateRange = [
        startDateRaw ? dayjs(startDateRaw).toISOString() : null,
        endDateRaw ? dayjs(endDateRaw).toISOString() : null,
      ];
    }

    if (filterItemForApiCall.includeTestCompanies) {
      activeFilters.includeTestCompanies = true;
    }

    if (Object.keys(activeFilters).length > 0) {
      dispatch(
        setAdditionalFilteringParameters({
          entity: "trackedService",
          filters: activeFilters,
        })
      );
    }
  }, [filterItemForApiCall]);

  const triggerRefresh = () => {
    setRefreshTable((prev) => !prev);
  };
  const handleDateChangeforApiCallItem = (dateRange: any) => {
    setFilterItemForApiCall((prevState: any) => ({
      ...prevState,
      dateRange: dateRange,
    }));
  };
  const handleDropDownChangeForApiCallItem = (filter: any) => {
    setFilterItemForApiCall((prevState: any) => ({
      ...prevState,
      filter: filter,
    }));
  };
  const handleIncludeTestCompanies = (event: any) => {
    setFilterItemForApiCall((prevState: any) => ({
      ...prevState,
      includeTestCompanies: event.target.checked,
    }));
  };

  const handleSuccessfulGet = (data: any) => {
    if (data && data.total_price_in_cents !== undefined) {
      setTotalPriceInCents(data.total_price_in_cents);
    }
  };

  useEffect(() => {
    getMainCategories(setMaincategoryOptionsForFiltering);
    getTrackedServiceType(setTrackedServiceTypeForFiltering);
    getTrackedServiceTemplate(setTrackedServiceTemplateForFiltering);
    getKaerEmployees(setKaerEmployeeForFiltering);
  }, []);

  const navigate = useNavigate();
  const trackedServiceColumns = useMemo(
    () =>
      getTrackedServiceColumns({
        t,
        styles,
        includeNavigationLinks: true,
        yesNoFilterOptions,
        statusFilterOptions,
        mainCategoryOptionsForFiltering,
        trackedServiceTypeForFiltering,
        trackedServiceTemplateForFiltering,
        kaerEmployeeForFiltering,
      }),
    [
      t,
      yesNoFilterOptions,
      statusFilterOptions,
      mainCategoryOptionsForFiltering,
      trackedServiceTypeForFiltering,
      trackedServiceTemplateForFiltering,
      kaerEmployeeForFiltering,
    ]
  );

  const setTrackedServiceBilled = async (row: any, skipQuestion = false) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("trackedservice_setbilled_confirm", {
          title: `${row.company_subject.name} - ${row.category.name}`,
        })
      );
      if (!isConfirmed) return;
    }

    const additionalRouteParts: any = {};
    additionalRouteParts["mark_billed"] = "/";

    api.genericApiRequest({
      method: "put",
      entity: "trackedService",
      entityId: row.id,
      additionalRouteParts: additionalRouteParts,
      successHandler: () => {
        triggerRefresh();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  const setTrackedServiceChecked = async (row: any, skipQuestion = false) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("trackedservice_setchecked_confirm", {
          title: `${row.company_subject.name} - ${row.category.name}`,
        })
      );
      if (!isConfirmed) return;
    }
    const additionalRouteParts: any = {};
    additionalRouteParts["mark_checked"] = "/";
    api.genericApiRequest({
      method: "put",
      entity: "trackedService",
      entityId: row.id,
      additionalRouteParts: additionalRouteParts,
      successHandler: () => {
        triggerRefresh();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const setTrackedServiceBackToEdit = async (
    row: any,
    skipQuestion = false
  ) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("trackedservice_back_to_edit_comfirm", {
          title: `${row.company_subject.name} - ${row.category.name}`,
        })
      );
      if (!isConfirmed) return;
    }
    const trackedServiceToReset = row.id;
    const additionalRouteParts: any = {};
    additionalRouteParts["reset_billed_and_checked"] = "/";
    api.genericApiRequest({
      method: "put",
      entity: "trackedService",
      entityId: trackedServiceToReset,
      additionalRouteParts: additionalRouteParts,
      successHandler: () => {
        triggerRefresh();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const setTrackedServiceStatus = async (
    selectedRows: any[],
    status: "checked" | "billed" | "reset",
    skipQuestion = false
  ) => {
    const selectedIds = selectedRows.map((row: any) => row.id);
    const confirmationMessage =
      status === "checked"
        ? t("trackedservice_setchecked_confirm_multi")
        : status === "billed"
        ? t("trackedservice_setbilled_confirm_multi")
        : t("trackedservice_back_to_edit_comfirm_multi");

    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(confirmationMessage);
      if (!isConfirmed) return;
    }

    const routePart =
      status === "checked"
        ? { mark_checked: "/" }
        : status === "billed"
        ? { mark_billed: "/" }
        : { reset_billed_and_checked: "/" };

    const result = { tracked_service_set: selectedIds };
    api.genericApiRequest({
      method: "put",
      entity: "trackedService",
      submitData: result,
      additionalRouteParts: routePart,
      successHandler: () => {
        triggerRefresh();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const customRowActions = (row: any) => {
    const shouldRenderBilledIcon =
      row.original?.checked_on != null && row.original?.billed_on == null;

    const shouldRenderCheckedIcon = row.original?.checked_on == null;

    const shouldRenderBackToEdit =
      row.original?.checked_on != null || row.original?.billed_on != null;

    return (
      <>
        {shouldRenderBilledIcon && (
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("trackedservice_setbilled")}
          >
            <span>
              <GenericButton
                onClick={() => setTrackedServiceBilled(row.original)}
                variant="icon"
                color="iconBase"
              >
                <EuroIcon />
              </GenericButton>
            </span>
          </Tooltip>
        )}
        {shouldRenderCheckedIcon && (
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("trackedservice_setchecked")}
          >
            <span>
              <GenericButton
                variant="icon"
                color="iconBase"
                onClick={() => setTrackedServiceChecked(row.original)}
              >
                <CheckIcon />
              </GenericButton>
            </span>
          </Tooltip>
        )}
        {shouldRenderBackToEdit && (
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("trackedservice_back_to_edit")}
          >
            <GenericButton
              variant="icon"
              color="iconBase"
              onClick={() => setTrackedServiceBackToEdit(row.original)}
            >
              <UndoIcon />
            </GenericButton>
          </Tooltip>
        )}
      </>
    );
  };
  const checkForShowFilter = () => {
    return (
      filterItemForApiCall.filter !== "" &&
      Boolean(filterItemForApiCall.dateRange?.some(Boolean))
    );
  };
  const handleClearFilter = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setFilterItemForApiCall((currentFilters: any) => ({
      ...currentFilters,
      filter: "executed_on",
      dateRange: [],
    }));
  };

  const handleResetAllFilters = () => {
    setFilterItemForApiCall({
      filter: "executed_on",
      includeTestCompanies: false,
      dateRange: [],
    });
  };

  const addBusinessDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    let daysToAdd = Math.abs(days);
    const direction = days > 0 ? 1 : -1;

    while (daysToAdd > 0) {
      result.setDate(result.getDate() + direction);
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        daysToAdd--;
      }
    }

    return result;
  };

  const getBusinessDaysBetween = (startDate: Date, endDate: Date): number => {
    let count = 0;
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      if (current.getDay() !== 0 && current.getDay() !== 6) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }

    return count;
  };

  const navigateDateRange = (direction: "left" | "right") => {
    if (!checkForShowFilter()) return;

    const [startDate, endDate] = filterItemForApiCall.dateRange;
    if (!startDate || !endDate) return;

    const businessDaysInRange = getBusinessDaysBetween(startDate, endDate);

    const daysToMove =
      direction === "left" ? -businessDaysInRange : businessDaysInRange;
    const newStartDate = addBusinessDays(startDate, daysToMove);
    const newEndDate = addBusinessDays(endDate, daysToMove);

    setFilterItemForApiCall((prevState: any) => ({
      ...prevState,
      dateRange: [newStartDate, newEndDate],
    }));
  };

  const customActionButton = () => {
    const startDate = filterItemForApiCall?.dateRange?.[0];
    const endDate = filterItemForApiCall?.dateRange?.[1];
    const filterLabelMap: Record<string, string> = {
      executed_on: t("executed_on"),
      checked_on: t("trackedservice_checked_on"),
      billed_on: t("trackedservice_billed_on"),
      created_on: t("created_on"),
    };

    const filterLabel =
      filterLabelMap[filterItemForApiCall.filter] ||
      t(filterItemForApiCall.filter);

    return (
      <div className={styles.customActionButtonWrapper}>
        <GenericButton onClick={() => setFilterModalOpen(true)}>
          {t("apply_filter")}
        </GenericButton>

        {checkForShowFilter() && (
          <DateRangeFilterChip
            filterLabel={filterLabel}
            startDate={startDate}
            endDate={endDate}
            onDelete={handleClearFilter}
            onClick={() => setFilterModalOpen(true)}
            onNavigateLeft={() => navigateDateRange("left")}
            onNavigateRight={() => navigateDateRange("right")}
          />
        )}
      </div>
    );
  };
  const customTopToolBarActionsOnRowSelection = (selectedRows: any) => {
    const allUnCheked = selectedRows.every(
      (row: any) => row.checked_by === null
    );

    const anyCheckedOrBilled = selectedRows.some(
      (row: any) => row.checked_by === null || row.billed_by !== null
    );
    const checkForReset = selectedRows.every(
      (row: any) => row.checked_by !== null || row.billed_by !== null
    );

    return (
      <>
        <GenericButton
          color="secondary"
          startIcon={<CheckIcon />}
          onClick={() => setTrackedServiceStatus(selectedRows, "checked")}
          disabled={!allUnCheked}
        >
          {t("trackedservice_setchecked")}
        </GenericButton>
        <GenericButton
          color="secondary"
          startIcon={<EuroIcon />}
          disabled={anyCheckedOrBilled}
          onClick={() => setTrackedServiceStatus(selectedRows, "billed")}
        >
          {t("trackedservice_setbilled")}
        </GenericButton>
        <GenericButton
          color="secondary"
          startIcon={<UndoIcon />}
          disabled={!checkForReset}
          onClick={() => setTrackedServiceStatus(selectedRows, "reset")}
        >
          {t("trackedservice_back_to_edit_multi")}
        </GenericButton>
      </>
    );
  };
  const customColumnVisibility = {
    created_on: false,
    birth_date: false,
  };

  const totalPriceElement = () => {
    if (totalPriceInCents === null) return null;

    return (
      <>
        <div className={styles.totalPriceContainer}>
          <span>{t("trackedservice_total_price_for_current_filters")}:</span>
          <span className={styles.totalPriceValue}>{totalPriceInCents} €</span>
        </div>
        <FormControlLabel
          control={
            <Checkbox
              checked={filterItemForApiCall?.includeTestCompanies}
              onChange={(e) => handleIncludeTestCompanies(e)}
              color="primary"
            />
          }
          label={t("include_testcompanies")}
        />
      </>
    );
  };
  const modalFilterOptions = [
    { value: "executed_on", label: t("executed_on") },
    { value: "created_on", label: t("created_on") },
    { value: "checked_on", label: t("trackedservice_checked_on") },
    { value: "billed_on", label: t("trackedservice_billed_on") },
  ];

  return (
    <RestrictedPage permissionRequired="frontend_view_tracked_service">
      <section>
        <GenericCrudTable
          csvExportUrl={"trackedService"}
          refreshTable={refreshTable}
          entity="trackedService"
          entityAdapter={trackedServiceEntityAdapter}
          csvExportHeaderDateFilter={globalAdditionalFilterParameters}
          columns={trackedServiceColumns}
          customRowActions={customRowActions}
          customColumnVisibility={customColumnVisibility}
          additionalCustomTopToolbarElementsForRowSelection={
            customTopToolBarActionsOnRowSelection
          }
          additionalCustomTopToolbarElements={totalPriceElement}
          additionalUrlParameter={additionalUrlParameters}
          enableRowSelection={true}
          showColumnFilterbyDefault={true}
          allowColumnFiltering={true}
          onSuccessfulGetHandler={handleSuccessfulGet}
          customTopToolBarAction={customActionButton}
          hasAdditionalFiltersToReset={
            checkForShowFilter() || filterItemForApiCall.includeTestCompanies
          }
          onResetAllFilters={handleResetAllFilters}
          allowOpenEditInNewTab
          customEditHandler={(row: any) => {
            const editUrl = `/trackedservice/editTrackedService/${row.original.id}`;

            if (row.allowOpenInNewTab) {
              window.open(editUrl, "_blank", "noopener,noreferrer");
            } else {
              navigate(editUrl);
            }
          }}
          customCreateHandler={() => {
            navigate(`/trackedservice/createTrackedService`);
          }}
        />
      </section>
      {filterModalOpen && (
        <DateRangeFilterModal
          open={filterModalOpen}
          initialDateRange={filterItemForApiCall.dateRange}
          initialFilter={filterItemForApiCall.filter}
          filterOptions={modalFilterOptions}
          allowSingleDate={true}
          onClose={() => setFilterModalOpen(false)}
          onApply={(dateRange, filter) => {
            handleDateChangeforApiCallItem(dateRange);
            if (filter) {
              handleDropDownChangeForApiCallItem(filter);
            }
            setFilterModalOpen(false);
          }}
        />
      )}
      {errorModal}
    </RestrictedPage>
  );
};

export default TrackedServiceMainTable;
